import React, { useState, useEffect } from 'react';
import {
  X,
  ArrowDownCircle,
  Banknote,
  ShoppingCart,
  Truck,
  Zap,
  Users,
  Building2,
  Receipt,
  RotateCcw,
  Unlock,
} from 'lucide-react';
import { toast } from 'react-toastify';

export interface WithdrawModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (
    amount: number,
    reason: string,
    kind?: string,
    reference?: string,
    openDrawer?: boolean
  ) => Promise<void> | void;
  loading?: boolean;
}

const CATEGORY_PRESETS = [
  { id: 'proveedor', label: 'Proveedor / Insumos', icon: ShoppingCart, placeholder: 'Ej. Compra de verduras, carne, pan o insumos' },
  { id: 'domicilio', label: 'Domicilio / Transporte', icon: Truck, placeholder: 'Ej. Pago a domiciliario o transporte' },
  { id: 'servicios', label: 'Servicios / Mantenimiento', icon: Zap, placeholder: 'Ej. Pago de gas, reparaciones o servicios' },
  { id: 'nomina', label: 'Adelanto / Nómina', icon: Users, placeholder: 'Ej. Anticipo o pago de turno a personal' },
  { id: 'gerencia', label: 'Retiro a Gerencia / Dueño', icon: Building2, placeholder: 'Ej. Retiro de utilidades o traslado a caja fuerte' },
  { id: 'gastos', label: 'Gasto Menor / Varios', icon: Receipt, placeholder: 'Ej. Elementos de aseo, papelería, hielo, etc.' },
];

const QUICK_AMOUNTS = [10_000, 20_000, 50_000, 100_000];

export const WithdrawModal: React.FC<WithdrawModalProps> = ({
  visible,
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [amountStr, setAmountStr] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('proveedor');
  const [reference, setReference] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(true);

  useEffect(() => {
    if (visible) {
      setAmountStr('');
      setReason('');
      setSelectedCategory('proveedor');
      setReference('');
      setOpenDrawer(true);
    }
  }, [visible]);

  if (!visible) return null;

  const numericAmount = Number(amountStr) || 0;

  const handleAddAmount = (addVal: number) => {
    setAmountStr((prev) => {
      const current = Number(prev) || 0;
      return String(current + addVal);
    });
  };

  const handleClearAmount = () => {
    setAmountStr('');
  };

  const handleCategorySelect = (presetId: string) => {
    setSelectedCategory(presetId);
  };

  const currentPreset = CATEGORY_PRESETS.find((p) => p.id === selectedCategory);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (numericAmount <= 0) {
      toast.warn('Por favor ingresa un monto válido mayor a 0');
      return;
    }
    if (!reason.trim()) {
      toast.warn('Por favor ingresa el motivo o concepto del retiro');
      return;
    }

    const categoryLabel = currentPreset ? currentPreset.label : selectedCategory;
    onConfirm(numericAmount, reason.trim(), categoryLabel, reference.trim(), openDrawer);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div
        className="w-full max-w-lg rounded-2xl border border-divider bg-surface p-6 shadow-2xl text-text transition-all scale-100"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-divider pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-500/10 text-rose-500 dark:bg-rose-500/20">
              <ArrowDownCircle size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Registrar Retiro de Caja</h2>
              <p className="text-xs text-muted">
                Anota salidas de dinero, compras de insumos o pagos a proveedores.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-lg p-1.5 text-muted hover:bg-surface-muted hover:text-text transition"
            aria-label="Cerrar modal"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Categoría / Tipo de Retiro */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted">
              Tipo de Egreso / Categoría
            </label>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORY_PRESETS.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategorySelect(cat.id)}
                    className={`flex items-center gap-2 rounded-xl p-2.5 text-left text-xs font-medium transition border ${
                      isSelected
                        ? 'border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-400 font-semibold shadow-sm'
                        : 'border-divider bg-surface-muted/50 hover:bg-surface-muted text-muted hover:text-text'
                    }`}
                  >
                    <Icon size={16} className={isSelected ? 'text-rose-500' : 'text-muted'} />
                    <span className="truncate">{cat.label.split(' / ')[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Monto Input & Presets */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wider text-muted">
                Monto a Retirar ($)
              </label>
              {numericAmount > 0 && (
                <span className="text-xs font-semibold text-rose-500 tabular-nums">
                  $ {numericAmount.toLocaleString('es-CO')} COP
                </span>
              )}
            </div>

            <div className="relative mt-1.5">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-muted">
                <Banknote size={20} />
              </div>
              <input
                type="number"
                inputMode="numeric"
                step="100"
                min="1"
                placeholder="0"
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                autoFocus
                className="w-full rounded-xl border border-divider bg-surface pl-10 pr-4 py-2.5 text-xl font-bold tracking-wide placeholder:text-muted/40 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
              />
            </div>

            {/* Quick buttons */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {QUICK_AMOUNTS.map((val) => (
                <button
                  key={val}
                  type="button"
                  onClick={() => handleAddAmount(val)}
                  className="rounded-lg border border-divider bg-surface-muted/60 px-2.5 py-1 text-xs font-medium hover:bg-surface-muted hover:text-rose-600 transition"
                >
                  +{val >= 1000 ? `${val / 1000}k` : val}
                </button>
              ))}
              {numericAmount > 0 && (
                <button
                  type="button"
                  onClick={handleClearAmount}
                  className="inline-flex items-center gap-1 rounded-lg border border-divider/60 px-2 py-1 text-xs text-muted hover:text-rose-500 transition"
                >
                  <RotateCcw size={12} /> Borrar
                </button>
              )}
            </div>
          </div>

          {/* Concepto / Motivo */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted">
              Motivo o Concepto <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={2}
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={currentPreset?.placeholder || 'Describe el motivo de la salida de dinero'}
              className="mt-1.5 w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm placeholder:text-muted/50 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20 resize-none"
            />
          </div>

          {/* Comprobante / Referencia (Opcional) */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted">
              N° Comprobante / Factura / Recibo <span className="text-muted/60 font-normal">(Opcional)</span>
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Ej. Factura #8492 o Recibo #12"
              className="mt-1.5 w-full rounded-xl border border-divider bg-surface px-3 py-2 text-sm placeholder:text-muted/50 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
            />
          </div>

          {/* Abrir Cajón Checkbox */}
          <div className="flex items-center gap-2.5 rounded-xl border border-divider/60 bg-surface-muted/30 p-3">
            <input
              type="checkbox"
              id="openDrawerCheckbox"
              checked={openDrawer}
              onChange={(e) => setOpenDrawer(e.target.checked)}
              className="h-4 w-4 rounded border-divider text-rose-600 focus:ring-rose-500 accent-rose-600 cursor-pointer"
            />
            <label htmlFor="openDrawerCheckbox" className="flex items-center gap-1.5 text-xs text-muted cursor-pointer select-none">
              <Unlock size={14} className="text-amber-500" />
              <span>Abrir cajón monedero automáticamente al registrar</span>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-divider">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="rounded-xl px-4 py-2.5 text-sm font-semibold text-muted hover:bg-surface-muted hover:text-text transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || numericAmount <= 0 || !reason.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-600/20 hover:bg-rose-700 active:scale-95 disabled:opacity-50 disabled:pointer-events-none transition"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                  <span>Guardando...</span>
                </>
              ) : (
                <>
                  <ArrowDownCircle size={18} />
                  <span>
                    Registrar {numericAmount > 0 ? `$ ${numericAmount.toLocaleString('es-CO')}` : 'Retiro'}
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawModal;
